import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import ChatWidget from './ChatWidget';
import AskNewAiWidget from './AskNewAiWidget';
import LocationFilterFloater from './LocationFilterFloater';
import Sidebar from './Sidebar';
import DemoAuth from './DemoAuth';
import PreviewModeBanner from './PreviewModeBanner';
import { useAuth } from '@/contexts/AuthContext';
import { useLocationFilter } from '@/contexts/LocationFilterContext';
import { useOrganizationType } from '@/hooks/useOrganizationType';
import { Filter, X } from 'lucide-react';

const Layout: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { isAuthenticated } = useAuth();
  const { hasActiveFilters, selectedLocationIds, availableLocations, clearFilters } = useLocationFilter();
  const orgTypeConfig = useOrganizationType();

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const getSelectedLocationNames = () => {
    return availableLocations
      .filter(loc => selectedLocationIds.includes(loc.id!))
      .map(loc => loc.name)
      .slice(0, 2);
  };

  const selectedNames = getSelectedLocationNames();
  const remainingCount = selectedLocationIds.length - selectedNames.length;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Preview Mode Banner */}
        <PreviewModeBanner />
        
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-2">
          <div className="flex items-center justify-between">
            {/* Left side - Location filter indicator */}
            <div className="flex items-center">
              {orgTypeConfig.features.locationFilter && hasActiveFilters && (
                <div className="flex items-center space-x-2 bg-primary-50 border border-primary-200 rounded-lg px-3 py-1.5">
                  <Filter className="w-4 h-4 text-primary-600" />
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-primary-900">
                      {selectedLocationIds.length} location{selectedLocationIds.length !== 1 ? 's' : ''} selected
                    </span>
                    {selectedNames.length > 0 && (
                      <span className="text-xs text-primary-700">
                        ({selectedNames.join(', ')}{remainingCount > 0 && ` + ${remainingCount} more`})
                      </span>
                    )}
                  </div>
                  <button
                    onClick={clearFilters}
                    className="ml-2 text-primary-600 hover:text-primary-700 hover:bg-primary-100 rounded p-0.5 transition-colors"
                    title="Clear location filters"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            
            {/* Right side - Organization selector and user profile */}
            <div className="flex items-center">
              {isAuthenticated ? (
                <DemoAuth />
              ) : (
                <DemoAuth />
              )}
            </div>
          </div>
        </div>
        
        {/* Page Content */}
        <main className="flex-1 min-h-0 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>

        {/* Floating Chat Widgets */}
        {/* <ChatWidget /> */}
        <AskNewAiWidget />
        
        {/* Floating Location Filter */}
        {orgTypeConfig.features.locationFilter && <LocationFilterFloater />}
      </div>
    </div>
  );
};

export default Layout;
